import {
  SITE_VISIT_SLOT_END_HOUR,
  SITE_VISIT_SLOT_START_HOUR,
  siteVisitSlotLabel,
  windowToSlot,
} from "@shared/siteVisitCalendarWindows";
import {
  SINGAPORE_TIME_ZONE,
  addSingaporeCalendarDays,
  getSingaporeDateKeyFromInstant,
  getSingaporeMinutesOfDay,
  parseSingaporeDateInput,
  singaporeCalendarDayBounds,
  singaporeCalendarDayFromInstant,
  singaporeCalendarRangeBounds,
  singaporeDateFromIsoDate,
  singaporeDateKeysEqual,
  singaporeTodayDateKey,
  toSingaporeDateInputValue,
} from "@shared/timezone";
import type { CalendarLeaveBlock, CalendarVisit, FieldVisitLane } from "./fieldCalendarTypes";
import { logFieldCalendarCrash } from "./fieldCalendarDiagnostics";

export function isValidDate(d: Date): boolean {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function singaporeWeekdayMonday0(d: Date): number {
  const wd = new Intl.DateTimeFormat("en-US", {
    timeZone: SINGAPORE_TIME_ZONE,
    weekday: "short",
  }).format(d);
  const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return map[wd] ?? 0;
}

/** Normalize any date input to noon SGT of its Singapore calendar day; invalid → today SGT. */
export function coerceValidDay(input: Date | string | number | null | undefined): Date {
  try {
    if (input == null) return singaporeDateFromIsoDate(singaporeTodayDateKey());
    if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
      return parseSingaporeDateInput(input.trim());
    }
    const day = singaporeCalendarDayFromInstant(input);
    if (day) return day;
    return singaporeDateFromIsoDate(singaporeTodayDateKey());
  } catch (error) {
    logFieldCalendarCrash("coerceValidDay", error, { input });
    return singaporeDateFromIsoDate(singaporeTodayDateKey());
  }
}

export function startOfDay(d: Date): Date {
  return coerceValidDay(d);
}

/** `YYYY-MM-DD` for `<input type="date">` — Singapore calendar day, never UTC ISO. */
export function localDateInputValue(d: Date): string {
  return toSingaporeDateInputValue(coerceValidDay(d));
}

/** Parse `YYYY-MM-DD` as noon SGT (canonical Service Portal appointment day). */
export function parseLocalDateInput(value: string): Date {
  try {
    return parseSingaporeDateInput(value);
  } catch {
    return singaporeDateFromIsoDate(singaporeTodayDateKey());
  }
}

export function addDays(d: Date, n: number): Date {
  const key = getSingaporeDateKeyFromInstant(coerceValidDay(d));
  if (!key) return singaporeDateFromIsoDate(singaporeTodayDateKey());
  return parseSingaporeDateInput(addSingaporeCalendarDays(key, n));
}

export function isSameDay(a: Date, b: Date): boolean {
  return singaporeDateKeysEqual(a, b);
}

/** Monday-first week containing `d` (Singapore weekday). */
export function startOfWeekMonday(d: Date): Date {
  const x = startOfDay(d);
  return addDays(x, -singaporeWeekdayMonday0(x));
}

export function endOfWeekSunday(weekStart: Date): Date {
  return addDays(weekStart, 6);
}

export function startOfMonth(d: Date): Date {
  const key = getSingaporeDateKeyFromInstant(coerceValidDay(d));
  if (!key) return singaporeDateFromIsoDate(singaporeTodayDateKey());
  const [y, m] = key.split("-");
  return parseSingaporeDateInput(`${y}-${m}-01`);
}

export function endOfMonth(d: Date): Date {
  const key = getSingaporeDateKeyFromInstant(coerceValidDay(d));
  if (!key) return singaporeDateFromIsoDate(singaporeTodayDateKey());
  const [y, m] = key.split("-").map(Number) as [number, number];
  const nextMonthKey =
    m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  return parseSingaporeDateInput(addSingaporeCalendarDays(nextMonthKey, -1));
}

/** 42 cells: full weeks covering the month (Mon-first, Singapore days). */
export function getMonthGridDays(monthAnchor: Date): Date[] {
  const first = startOfMonth(monthAnchor);
  const gridStart = startOfWeekMonday(first);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

export function getWeekDays(weekAnchor: Date): Date[] {
  const start = startOfWeekMonday(weekAnchor);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

function coerceVisitId(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function sanitizeVisit(raw: unknown): CalendarVisit | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as CalendarVisit;
  const id = coerceVisitId(v.id);
  if (id == null) return null;
  return { ...v, id };
}

export function sanitizeVisits(raw: unknown): CalendarVisit[] {
  if (!Array.isArray(raw)) return [];
  const out: CalendarVisit[] = [];
  for (const item of raw) {
    const v = sanitizeVisit(item);
    if (v) out.push(v);
  }
  return out;
}

export function sanitizeLeaveBlocks(raw: unknown): CalendarLeaveBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((lb): lb is CalendarLeaveBlock => {
    if (!lb || typeof lb !== "object") return false;
    return typeof (lb as CalendarLeaveBlock).id === "number";
  });
}

/** Singapore calendar day key (`YYYY-MM-DD`) for visit grouping and grid cells. */
export function dateKey(d: Date): string {
  if (!isValidDate(d)) return getSingaporeDateKeyFromInstant(new Date()) ?? "";
  return getSingaporeDateKeyFromInstant(d) ?? "";
}

/** Calendar column for a visit: SGT day of check-in, else scheduled visitDate. */
export function visitPlacementDate(v: CalendarVisit): Date {
  try {
    if (v.arrivedAt) {
      const day = singaporeCalendarDayFromInstant(v.arrivedAt);
      if (day) return day;
    }
    const scheduled = singaporeCalendarDayFromInstant(v.visitDate);
    if (scheduled) return scheduled;
    const vd = new Date(v.visitDate);
    if (isValidDate(vd)) return vd;
  } catch (error) {
    logFieldCalendarCrash("visitPlacementDate", error, { visitId: v?.id });
  }
  return new Date();
}

export function getVisitLane(v: CalendarVisit | null | undefined): FieldVisitLane {
  if (!v) return "others";
  if (v.visitType === "urgent") return "urgent";
  if (v.status === "closed" || v.status === "waived") return "completed";
  const assignees = Array.isArray(v.assignees) ? v.assignees : [];
  const role = assignees[0]?.role ?? "technician";
  if (role === "installer") return "installation";
  if (role === "programmer") return "programming";
  if (role === "technician") return "service";
  return "others";
}

export function visitDisplayLabel(v: CalendarVisit | null | undefined): string {
  if (!v) return "Visit";
  const name = v.customerName?.trim();
  if (name) {
    const short = name.length > 14 ? `${name.slice(0, 12)}…` : name;
    return short;
  }
  const addr = v.customerAddress?.trim();
  if (addr) {
    const short = addr.length > 14 ? `${addr.slice(0, 12)}…` : addr;
    return short;
  }
  return v.visitNumber;
}

export function visitTimeRange(v: CalendarVisit | null | undefined): {
  startMins: number;
  endMins: number;
  label: string;
} | null {
  if (!v) return null;
  const START_MINS = SITE_VISIT_SLOT_START_HOUR * 60;

  try {
  if (v.arrivedAt) {
    let startMins = getSingaporeMinutesOfDay(v.arrivedAt) ?? 0;
    let endMins: number;
    if (v.departedAt) {
      endMins = getSingaporeMinutesOfDay(v.departedAt) ?? startMins + 30;
    } else {
      endMins = getSingaporeMinutesOfDay(new Date()) ?? startMins + 30;
      if (endMins <= startMins) endMins = startMins + 30;
    }
    const fmt = (m: number) => {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const ampm = h < 12 ? "AM" : "PM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${min === 0 ? "00" : String(min).padStart(2, "0")} ${ampm}`;
    };
    return { startMins, endMins, label: `${fmt(startMins)} – ${fmt(endMins)}` };
  }

  const win = v.arrivalWindow ?? "";
  const rangeMatch = win.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (rangeMatch) {
    const startMins = Number(rangeMatch[1]) * 60 + Number(rangeMatch[2]);
    let endMins = Number(rangeMatch[3]) * 60 + Number(rangeMatch[4]);
    if (endMins <= startMins) endMins = startMins + 30;
    const fmt = (m: number) => {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const ampm = h < 12 ? "AM" : "PM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${min === 0 ? "00" : String(min).padStart(2, "0")} ${ampm}`;
    };
    return { startMins, endMins, label: `${fmt(startMins)} – ${fmt(endMins)}` };
  }

  const slotIdx = win ? windowToSlot(win) : -1;
  if (slotIdx < 0) return null;
  const startMins = START_MINS + slotIdx * 30;
  const endMins = startMins + 30;
  return {
    startMins,
    endMins,
    label: siteVisitSlotLabel(slotIdx),
  };
  } catch (error) {
    logFieldCalendarCrash("visitTimeRange", error, { visitId: v.id });
    return null;
  }
}

export function filterVisitsForFieldUser(
  visits: CalendarVisit[] | null | undefined,
  opts: {
    mySchedule: boolean;
    userId?: number;
    laneFilter: import("./fieldCalendarTypes").FieldVisitFilter;
  },
): CalendarVisit[] {
  let list = Array.isArray(visits) ? visits : [];
  if (opts.mySchedule && opts.userId) {
    list = list.filter((v) => {
      const assignees = Array.isArray(v.assignees) ? v.assignees : [];
      if (assignees.some((a) => a?.userId === opts.userId)) return true;
      return v.technicianId === opts.userId;
    });
  }
  if (opts.laneFilter === "all") return list;
  return list.filter((v) => {
    const lane = getVisitLane(v);
    if (lane === "completed" || lane === "urgent") return true;
    return lane === opts.laneFilter;
  });
}

export function groupVisitsByDate(
  visits: CalendarVisit[] | null | undefined,
): Record<string, CalendarVisit[]> {
  const map: Record<string, CalendarVisit[]> = {};
  const list = Array.isArray(visits) ? visits : [];
  for (const raw of list) {
    const v = sanitizeVisit(raw);
    if (!v) continue;
    try {
      const key = dateKey(visitPlacementDate(v));
      if (!map[key]) map[key] = [];
      map[key].push(v);
    } catch (error) {
      logFieldCalendarCrash("groupVisitsByDate", error, { visitId: raw?.id });
    }
  }
  for (const key of Object.keys(map)) {
    map[key].sort((a, b) => {
      const ta = visitTimeRange(a)?.startMins ?? 0;
      const tb = visitTimeRange(b)?.startMins ?? 0;
      return ta - tb;
    });
  }
  return map;
}

export function leaveBlocksForDate(
  blocks: CalendarLeaveBlock[] | null | undefined,
  day: Date,
  userId?: number,
): CalendarLeaveBlock[] {
  const list = Array.isArray(blocks) ? blocks : [];
  const safeDay = coerceValidDay(day);
  const key = dateKey(safeDay);
  return list.filter((lb) => {
    try {
      if (userId != null && lb.userId !== userId) return false;
      const ld = new Date(lb.leaveDate);
      if (!isValidDate(ld)) return false;
      return dateKey(startOfDay(ld)) === key;
    } catch {
      return false;
    }
  });
}

export function queryRangeForView(
  viewMode: import("./fieldCalendarTypes").FieldCalendarViewMode,
  anchor: Date,
  dayFocus: Date,
): { startDate: Date; endDate: Date } {
  const safeAnchor = coerceValidDay(anchor);
  const safeFocus = coerceValidDay(dayFocus);
  try {
    if (viewMode === "day") {
      const { startInclusive, endExclusive } = singaporeCalendarDayBounds(safeFocus);
      // endDate is exclusive (next Singapore midnight).
      return { startDate: startInclusive, endDate: endExclusive };
    }
    if (viewMode === "week") {
      const first = startOfWeekMonday(safeAnchor);
      const last = endOfWeekSunday(first);
      const { startInclusive, endExclusive } = singaporeCalendarRangeBounds(first, last);
      return { startDate: startInclusive, endDate: endExclusive };
    }
    const grid = getMonthGridDays(safeAnchor);
    const first = grid[0] ?? safeAnchor;
    const last = grid[grid.length - 1] ?? safeAnchor;
    const { startInclusive, endExclusive } = singaporeCalendarRangeBounds(first, last);
    return { startDate: startInclusive, endDate: endExclusive };
  } catch (error) {
    logFieldCalendarCrash("queryRangeForView", error, { viewMode });
    const { startInclusive, endExclusive } = singaporeCalendarDayBounds(new Date());
    return { startDate: startInclusive, endDate: endExclusive };
  }
}

export const SLOT_START_HOUR = SITE_VISIT_SLOT_START_HOUR;
export const SLOT_END_HOUR = SITE_VISIT_SLOT_END_HOUR;

/** Dispatch board geometry */
export const DISPATCH_START_HOUR = SITE_VISIT_SLOT_START_HOUR;
export const DISPATCH_END_HOUR = 19;
export const DISPATCH_PX_PER_HOUR = 72;
export const DISPATCH_TIMELINE_WIDTH =
  (DISPATCH_END_HOUR - DISPATCH_START_HOUR) * DISPATCH_PX_PER_HOUR;
export const DISPATCH_ROW_HEIGHT = 56;

export function viewModeTitle(mode: "month" | "week" | "day"): string {
  if (mode === "week") return "Calendar Week View";
  if (mode === "day") return "Calendar Day View";
  return "Calendar Month View";
}

export function formatMinutesLabel(mins: number): string {
  if (!Number.isFinite(mins)) return "—";
  const safe = Math.max(0, Math.min(mins, 24 * 60 - 1));
  const h = Math.floor(safe / 60);
  const min = safe % 60;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${min === 0 ? "00" : String(min).padStart(2, "0")} ${ampm}`;
}

export function getVisitPrimaryAssignee(v: CalendarVisit | null | undefined): {
  userId: number;
  userName: string;
  role: string;
} {
  if (!v) return { userId: 0, userName: "Unassigned", role: "technician" };
  const assignees = Array.isArray(v.assignees) ? v.assignees : [];
  const a = assignees[0];
  if (a) {
    return {
      userId: a.userId ?? 0,
      userName: a.userName?.trim() || "Technician",
      role: a.role ?? "technician",
    };
  }
  if (v.technicianId != null) {
    return {
      userId: v.technicianId,
      userName: v.technicianName ?? "Technician",
      role: "technician",
    };
  }
  return { userId: 0, userName: "Unassigned", role: "technician" };
}

export type TimedVisitLayout = {
  visit: CalendarVisit;
  startMins: number;
  endMins: number;
  column: number;
  columnCount: number;
};

/** Stagger overlapping visits into readable columns (execution panel + dispatch rows). */
export function layoutTimedVisits(visits: CalendarVisit[] | null | undefined): TimedVisitLayout[] {
  try {
    const list = Array.isArray(visits) ? visits : [];
    const items = list
      .map((raw) => {
        const visit = sanitizeVisit(raw);
        if (!visit) return null;
        const range = visitTimeRange(visit);
        if (!range || !Number.isFinite(range.startMins) || !Number.isFinite(range.endMins)) {
          return null;
        }
        return { visit, startMins: range.startMins, endMins: range.endMins };
      })
      .filter((x): x is NonNullable<typeof x> => x != null)
      .sort((a, b) => a.startMins - b.startMins || a.endMins - b.endMins);

    const columnEnds: number[] = [];
    const placed: TimedVisitLayout[] = [];

    for (const item of items) {
      let col = columnEnds.findIndex((end) => end <= item.startMins);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(item.endMins);
      } else {
        columnEnds[col] = item.endMins;
      }
      placed.push({ ...item, column: col, columnCount: 1 });
    }

    const maxCols = Math.max(1, columnEnds.length);
    return placed.map((p) => ({ ...p, columnCount: maxCols }));
  } catch (error) {
    logFieldCalendarCrash("layoutTimedVisits", error);
    return [];
  }
}

export const FIELD_STAFF_PORTAL_ROLES = new Set(["service", "programming", "installer"]);

export function portalRoleToAssigneeRole(
  role: string | null | undefined,
): "technician" | "programmer" | "installer" {
  const r = (role ?? "").toLowerCase();
  if (r === "programming") return "programmer";
  if (r === "installer") return "installer";
  return "technician";
}

export type FieldStaffScheduleRow = {
  userId: number;
  userName: string;
  role: string;
  visits: CalendarVisit[];
  onLeave: CalendarLeaveBlock | null;
};

export type FieldStaffRosterMember = {
  id: number;
  name: string | null | undefined;
  role: string;
};

/** All active field staff rows for a day, with visits and leave overlaid. */
export function buildFieldStaffScheduleRows(
  dayVisits: CalendarVisit[],
  dayLeave: CalendarLeaveBlock[],
  fieldStaff: FieldStaffRosterMember[],
): FieldStaffScheduleRow[] {
  const map = new Map<number, FieldStaffScheduleRow>();

  for (const member of fieldStaff) {
    if (!FIELD_STAFF_PORTAL_ROLES.has(member.role)) continue;
    map.set(member.id, {
      userId: member.id,
      userName: (member.name ?? "").trim() || `User ${member.id}`,
      role: portalRoleToAssigneeRole(member.role),
      visits: [],
      onLeave: null,
    });
  }

  for (const row of groupVisitsByTechnician(dayVisits)) {
    const key = row.userId || -1;
    const existing = map.get(key);
    if (existing) {
      existing.visits = row.visits;
      if (row.userName) existing.userName = row.userName;
      if (row.role) existing.role = row.role;
    } else {
      map.set(key, { ...row, onLeave: null });
    }
  }

  for (const lb of dayLeave) {
    const existing = map.get(lb.userId);
    if (existing) {
      existing.onLeave = lb;
      if (lb.userName) existing.userName = lb.userName;
    } else {
      map.set(lb.userId, {
        userId: lb.userId,
        userName: lb.userName ?? "Technician",
        role: "technician",
        visits: [],
        onLeave: lb,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    a.userName.localeCompare(b.userName, undefined, { sensitivity: "base" }),
  );
}

export function groupVisitsByTechnician(
  visits: CalendarVisit[] | null | undefined,
): { userId: number; userName: string; role: string; visits: CalendarVisit[] }[] {
  try {
    const list = Array.isArray(visits) ? visits : [];
    const map = new Map<
      number,
      { userId: number; userName: string; role: string; visits: CalendarVisit[] }
    >();
    for (const raw of list) {
      const v = sanitizeVisit(raw);
      if (!v) continue;
      const uniqueAssignees = new Map<number, ReturnType<typeof getVisitPrimaryAssignee>>();
      for (const assignee of Array.isArray(v.assignees) ? v.assignees : []) {
        if (!Number.isFinite(assignee?.userId) || assignee.userId <= 0) continue;
        if (!uniqueAssignees.has(assignee.userId)) {
          uniqueAssignees.set(assignee.userId, {
            userId: assignee.userId,
            userName: assignee.userName?.trim() || "Technician",
            role: assignee.role ?? "technician",
          });
        }
      }

      const assignees =
        uniqueAssignees.size > 0
          ? Array.from(uniqueAssignees.values())
          : [getVisitPrimaryAssignee(v)];

      for (const assignee of assignees) {
        const key = assignee.userId || -v.id;
        if (!map.has(key)) {
          map.set(key, {
            userId: assignee.userId,
            userName: assignee.userName,
            role: assignee.role,
            visits: [],
          });
        }
        map.get(key)!.visits.push(v);
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.userName ?? "").localeCompare(b.userName ?? "", undefined, { sensitivity: "base" }),
    );
  } catch (error) {
    logFieldCalendarCrash("groupVisitsByTechnician", error);
    return [];
  }
}

export function dispatchLeftPx(startMins: number): number {
  if (!Number.isFinite(startMins)) return 0;
  const origin = DISPATCH_START_HOUR * 60;
  return Math.max(0, ((startMins - origin) / 60) * DISPATCH_PX_PER_HOUR);
}

export function dispatchWidthPx(startMins: number, endMins: number): number {
  if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) return 64;
  const duration = Math.max(endMins - startMins, 30);
  return Math.max(64, (duration / 60) * DISPATCH_PX_PER_HOUR);
}

export function buildDateStrip(focus: Date, count = 14): Date[] {
  try {
    const safe = coerceValidDay(focus);
    const n = Math.max(1, Math.min(count, 31));
    const start = addDays(safe, -Math.floor(n / 2));
    return Array.from({ length: n }, (_, i) => addDays(start, i)).filter(isValidDate);
  } catch (error) {
    logFieldCalendarCrash("buildDateStrip", error);
    return [coerceValidDay(new Date())];
  }
}
